import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Add as AddIcon,
  Sync as SyncIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  TrendingDown as StockIcon,
  ShoppingCart as OrderIcon,
  Business as SupplierIcon
} from '@mui/icons-material';

interface SupplyChainIntegrationsProps {
  integrations: any[];
  onRefresh: () => void;
  loading: boolean;
}

const SupplyChainIntegrations: React.FC<SupplyChainIntegrationsProps> = ({
  integrations,
  onRefresh,
  loading
}) => {
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState('');
  const [syncSettings, setSyncSettings] = useState({
    sync_inventory: true,
    sync_orders: true,
    sync_suppliers: true,
    sync_pricing: true,
    auto_reorder: false,
    frequency: 'HOURLY'
  });

  // Supply Chain Systems
  const supplyChainSystems = [
    {
      name: 'TradeGecko',
      logo: '🦎',
      description: 'Comprehensive inventory management with multi-channel support',
      features: ['Inventory Tracking', 'Order Management', 'Supplier Portal', 'Multi-location'],
      pricing: 'From $39/month',
      status: 'available'
    },
    {
      name: 'Cin7',
      logo: '📦',
      description: 'Inventory and order management for growing businesses',
      features: ['Stock Control', 'Purchase Orders', 'Warehouse Management', 'B2B Portal'],
      pricing: 'From $299/month',
      status: 'available'
    },
    {
      name: 'Unleashed',
      logo: '🚀',
      description: 'Cloud inventory software with manufacturing capabilities',
      features: ['Real-time Stock', 'Manufacturing', 'Lot Tracking', 'Mobile App'],
      pricing: 'From $380/month',
      status: 'available'
    },
    {
      name: 'inFlow',
      logo: '📊',
      description: 'Inventory management system for small to medium businesses',
      features: ['Barcode Scanning', 'Low Stock Alerts', 'Vendor Management', 'Reporting'],
      pricing: 'From $71/month',
      status: 'available'
    }
  ];

  // Mock supply chain sync data
  const supplyChainData = {
    products_synced: 2847,
    orders_synced: 156,
    suppliers_synced: 23,
    low_stock_alerts: 12,
    last_sync: '2024-09-15T09:15:00Z',
    sync_errors: 0
  };

  // Mock inventory data flow
  const inventoryDataFlow = [
    {
      source: 'Inventory System',
      destination: 'Financial System',
      data_type: 'Stock Levels',
      records: 2847,
      last_sync: '2024-09-15T09:15:00Z',
      status: 'SUCCESS'
    },
    {
      source: 'Inventory System',
      destination: 'Financial System',
      data_type: 'Purchase Orders',
      records: 45,
      last_sync: '2024-09-15T09:10:00Z',
      status: 'SUCCESS'
    },
    {
      source: 'Financial System',
      destination: 'Inventory System',
      data_type: 'Cost Updates',
      records: 156,
      last_sync: '2024-09-15T09:05:00Z',
      status: 'SUCCESS'
    },
    {
      source: 'Inventory System',
      destination: 'Financial System',
      data_type: 'Supplier Invoices',
      records: 23,
      last_sync: '2024-09-15T09:00:00Z',
      status: 'SUCCESS'
    }
  ];

  const handleSetupSystem = (systemName: string) => {
    setSelectedSystem(systemName);
    setShowSetupDialog(true);
  };

  const handleConnect = () => {
    // Simulate system connection
    setShowSetupDialog(false);
    onRefresh();
  };

  return (
    <Box>
      {/* Supply Chain Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <InventoryIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Products Synced</Typography>
                  <Typography variant="h6">{supplyChainData.products_synced.toLocaleString()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <OrderIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Orders Synced</Typography>
                  <Typography variant="h6">{supplyChainData.orders_synced}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <SupplierIcon color="info" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Suppliers</Typography>
                  <Typography variant="h6">{supplyChainData.suppliers_synced}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <StockIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Low Stock Alerts</Typography>
                  <Typography variant="h6" color="warning.main">{supplyChainData.low_stock_alerts}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Inventory Data Flow */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Inventory Data Flow
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Data Type</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell>Last Sync</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryDataFlow.map((flow, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {flow.source}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {flow.destination}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {flow.data_type}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {flow.records.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(flow.last_sync).toLocaleString('en-US')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={flow.status}
                        color={flow.status === 'SUCCESS' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Low Stock Alerts */}
      {supplyChainData.low_stock_alerts > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Low Stock Alert:</strong> {supplyChainData.low_stock_alerts} products are running low on stock. 
            Consider setting up automatic reorder points to prevent stockouts.
          </Typography>
        </Alert>
      )}

      {/* Available Supply Chain Systems */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Available Supply Chain Systems
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            {supplyChainSystems.map((system, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Typography variant="h4">
                        {system.logo}
                      </Typography>
                      <Box>
                        <Typography variant="h6">
                          {system.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {system.pricing}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {system.description}
                    </Typography>
                    
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Key Features:
                    </Typography>
                    <List dense>
                      {system.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleSetupSystem(system.name)}
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Connect {system.name}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* System Setup Dialog */}
      <Dialog open={showSetupDialog} onClose={() => setShowSetupDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InventoryIcon />
            Connect {selectedSystem}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Inventory Integration:</strong> Connect your {selectedSystem} system to automatically sync 
              inventory levels, purchase orders, and supplier information with your financial records.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="API Endpoint"
                placeholder="https://api.example.com"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="API Key"
                placeholder="Enter your API key"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Warehouse Location"
                placeholder="Main warehouse identifier"
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Sync Settings
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.sync_inventory}
                    onChange={(e) => setSyncSettings({...syncSettings, sync_inventory: e.target.checked})}
                  />
                }
                label="Sync Inventory Levels"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.sync_orders}
                    onChange={(e) => setSyncSettings({...syncSettings, sync_orders: e.target.checked})}
                  />
                }
                label="Sync Purchase Orders"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.sync_suppliers}
                    onChange={(e) => setSyncSettings({...syncSettings, sync_suppliers: e.target.checked})}
                  />
                }
                label="Sync Supplier Data"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.auto_reorder}
                    onChange={(e) => setSyncSettings({...syncSettings, auto_reorder: e.target.checked})}
                  />
                }
                label="Auto Reorder Alerts"
              />
            </Grid>
          </Grid>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Sync Frequency</InputLabel>
            <Select
              value={syncSettings.frequency}
              label="Sync Frequency"
              onChange={(e) => setSyncSettings({...syncSettings, frequency: e.target.value})}
            >
              <MenuItem value="REAL_TIME">Real-time</MenuItem>
              <MenuItem value="HOURLY">Hourly</MenuItem>
              <MenuItem value="DAILY">Daily</MenuItem>
              <MenuItem value="WEEKLY">Weekly</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSetupDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConnect}>
            Connect & Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplyChainIntegrations;
